/**
 * AllConversations — unified history of every chat across the app.
 *
 * Pulls `GET /api/conversations/all` (aggregator across ai_hub_threads,
 * cfo_conversations, quick_chat_conversations, tax_plans) and renders a
 * filterable, searchable list with source badges + deep-link "Open" buttons.
 *
 * Lives at `/all-conversations` (HOME nav section).
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { TagEditorPopover } from '../components/TagEditorPopover';
import {
  MessageSquare, Search, Trash2, ArrowRight, Sparkles, BrainCircuit,
  MessageCircle, Calculator, Clock, RefreshCw, Pin, Tag, X, FolderPlus, Folder,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Source → display metadata
const SOURCE_META = {
  ai_studio: {
    label: 'AI Studio',
    icon: Sparkles,
    badgeClass: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  },
  cfo_buck: {
    label: 'CFO Buck',
    icon: BrainCircuit,
    badgeClass: 'bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/30',
  },
  quick_chat: {
    label: 'Quick Chat',
    icon: MessageCircle,
    badgeClass: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
  },
  tax_plan: {
    label: 'Tax Plan',
    icon: Calculator,
    badgeClass: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
  },
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (new Date() - new Date(iso)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function AllConversations() {
  const navigate = useNavigate();
  const [data, setData] = useState({ conversations: [], total: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeSource, setActiveSource] = useState('all');
  const [activeTag, setActiveTag] = useState(null); // string | null
  const [allTags, setAllTags] = useState([]);       // [{tag, count}]
  const [selectedKeys, setSelectedKeys] = useState(new Set()); // "source:id"
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(null);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/api/conversations/folders`);
      if (res.ok) setFolders((await res.json()).folders || []);
    } catch (e) { console.error(e); }
  }, []);

  const applyFolder = (f) => {
    setActiveFolderId(f.folder_id);
    setActiveSource(f.source || 'all');
    setActiveTag(f.tag || null);
    setSearch(f.q || '');
  };

  const handleSaveFolder = async () => {
    const name = window.prompt('Name this folder:', activeTag ? `#${activeTag}` : (search.trim() || 'Filter'));
    if (!name) return;
    try {
      const res = await apiFetch(`${API}/api/conversations/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          source: activeSource === 'all' ? null : activeSource,
          tag: activeTag,
          q: search.trim() || null,
        }),
      });
      if (res.ok) {
        toast.success('Folder saved');
        fetchFolders();
      } else { toast.error('Save failed'); }
    } catch { toast.error('Save failed'); }
  };

  const handleDeleteFolder = async (folder_id, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this folder?')) return;
    try {
      const res = await apiFetch(`${API}/api/conversations/folders/${folder_id}`, { method: 'DELETE' });
      if (res.ok) {
        if (activeFolderId === folder_id) setActiveFolderId(null);
        setFolders((prev) => prev.filter((f) => f.folder_id !== folder_id));
        toast.success('Folder deleted');
      }
    } catch { toast.error('Delete failed'); }
  };

  const hasActiveFilter = search.trim() || activeTag || activeSource !== 'all';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('q', search.trim());
      if (activeTag) params.set('tag', activeTag);
      const res = await apiFetch(`${API}/api/conversations/all?${params.toString()}`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [search, activeTag]);

  const fetchTags = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/api/conversations/tags`);
      if (res.ok) {
        const json = await res.json();
        setAllTags(json.tags || []);
      }
    } catch (e) { console.error(e); }
  }, []);

  // Debounced refetch on search / tag change
  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [fetchData]);

  // Tag list refreshed on mount + after any tag-mutating action
  useEffect(() => { fetchTags(); }, [fetchTags]);
  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  const filtered = useMemo(
    () => activeSource === 'all'
      ? data.conversations
      : data.conversations.filter((c) => c.source === activeSource),
    [data.conversations, activeSource],
  );

  const selectionKey = (c) => `${c.source}:${c.id}`;

  const toggleSelect = (c) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const k = selectionKey(c);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  const clearSelection = () => setSelectedKeys(new Set());

  const selectAllVisible = () => {
    setSelectedKeys(new Set(filtered.map(selectionKey)));
  };

  const selectedItems = useMemo(
    () => filtered.filter((c) => selectedKeys.has(selectionKey(c))),
    [filtered, selectedKeys],
  );

  const handleSetTags = async (conv, newTags) => {
    // Optimistic
    setData((prev) => ({
      ...prev,
      conversations: prev.conversations.map((c) =>
        (c.id === conv.id && c.source === conv.source) ? { ...c, tags: newTags } : c,
      ),
    }));
    try {
      const res = await apiFetch(`${API}/api/conversations/${conv.source}/${conv.id}/tags`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tags: newTags }),
      });
      if (res.ok) {
        toast.success('Tags updated');
        fetchTags();
      } else {
        toast.error('Tag update failed');
        fetchData();
      }
    } catch {
      toast.error('Tag update failed');
      fetchData();
    }
  };

  const bulkPayload = () => ({ items: selectedItems.map((c) => ({ source: c.source, id: c.id })) });

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedItems.length} conversation(s)? This cannot be undone.`)) return;
    try {
      const res = await apiFetch(`${API}/api/conversations/bulk-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bulkPayload()),
      });
      if (res.ok) {
        const json = await res.json();
        toast.success(`Deleted ${json.deleted}`);
        clearSelection();
        fetchData();
        fetchTags();
      } else { toast.error('Bulk delete failed'); }
    } catch { toast.error('Bulk delete failed'); }
  };

  const handleBulkPin = async (pinned) => {
    try {
      const res = await apiFetch(`${API}/api/conversations/bulk-pin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...bulkPayload(), pinned }),
      });
      if (res.ok) {
        const json = await res.json();
        toast.success(`${pinned ? 'Pinned' : 'Unpinned'} ${json.updated}`);
        clearSelection();
        fetchData();
      } else { toast.error('Bulk pin failed'); }
    } catch { toast.error('Bulk pin failed'); }
  };

  const handleBulkAddTag = async () => {
    const tag = window.prompt('Add which tag to selected? (lowercase, ≤32 chars)');
    if (!tag) return;
    try {
      const res = await apiFetch(`${API}/api/conversations/bulk-tag`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...bulkPayload(), tag, operation: 'add' }),
      });
      if (res.ok) {
        const json = await res.json();
        toast.success(`Added "${json.tag}" to ${json.updated}`);
        clearSelection();
        fetchData();
        fetchTags();
      } else { toast.error('Bulk tag failed'); }
    } catch { toast.error('Bulk tag failed'); }
  };

  const handleDelete = async (e, conv) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${conv.title}"? This cannot be undone.`)) return;
    try {
      const res = await apiFetch(`${API}/api/conversations/${conv.source}/${conv.id}`, { method: 'DELETE' });
      if (res.ok) {
        setData((prev) => ({
          ...prev,
          conversations: prev.conversations.filter((c) => !(c.id === conv.id && c.source === conv.source)),
          total: Math.max(0, prev.total - 1),
        }));
        toast.success('Conversation deleted');
      } else {
        toast.error('Delete failed');
      }
    } catch { toast.error('Delete failed'); }
  };

  const handleTogglePin = async (e, conv) => {
    e.stopPropagation();
    const next = !conv.pinned;
    // Optimistic update
    setData((prev) => ({
      ...prev,
      conversations: prev.conversations.map((c) =>
        (c.id === conv.id && c.source === conv.source) ? { ...c, pinned: next } : c,
      ).sort((a, b) => {
        if (!!b.pinned !== !!a.pinned) return b.pinned ? 1 : -1;
        return (b.updated_at || '').localeCompare(a.updated_at || '');
      }),
    }));
    try {
      const res = await apiFetch(`${API}/api/conversations/${conv.source}/${conv.id}/pin`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinned: next }),
      });
      if (res.ok) {
        toast.success(next ? 'Pinned to top' : 'Unpinned');
      } else {
        toast.error('Pin failed');
        fetchData();
      }
    } catch {
      toast.error('Pin failed');
      fetchData();
    }
  };

  const SourceTab = ({ value, label, count }) => (
    <button
      type="button"
      onClick={() => setActiveSource(value)}
      className={cn(
        'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors border',
        activeSource === value
          ? 'bg-foreground text-background border-foreground'
          : 'text-muted-foreground hover:text-foreground border-border hover:border-muted-foreground/40',
      )}
      data-testid={`all-conversations-tab-${value}`}
    >
      {label}
      <span className={cn(
        'text-[9px] font-mono px-1 rounded',
        activeSource === value ? 'bg-background/20' : 'bg-muted',
      )}>
        {count}
      </span>
    </button>
  );

  return (
    <div className="space-y-6" data-testid="all-conversations-page">
      {/* Hero */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">All Conversations</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every chat & saved plan across CountBucks. Filter by source, search by title, jump back into context.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="gap-1.5" data-testid="all-conversations-refresh">
          <RefreshCw className={cn('w-3.5 h-3.5', loading && 'animate-spin')} /> Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search titles…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="all-conversations-search"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <SourceTab value="all" label="All" count={data.total} />
          <SourceTab value="ai_studio" label="AI Studio" count={data.counts?.ai_studio || 0} />
          <SourceTab value="cfo_buck" label="CFO Buck" count={data.counts?.cfo_buck || 0} />
          <SourceTab value="quick_chat" label="Quick Chat" count={data.counts?.quick_chat || 0} />
          <SourceTab value="tax_plan" label="Tax Plan" count={data.counts?.tax_plan || 0} />
        </div>
      </div>

      {/* Smart Folders row */}
      {(folders.length > 0 || hasActiveFilter) && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="folders-row">
          <Folder className="w-3 h-3 text-muted-foreground" />
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mr-1">Folders</span>
          {folders.map((f) => (
            <div
              key={f.folder_id}
              className={cn(
                'group flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors border',
                activeFolderId === f.folder_id
                  ? 'bg-violet-500 text-white border-violet-500'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted border-transparent',
              )}
            >
              <button type="button" onClick={() => applyFolder(f)} data-testid={`folder-${f.folder_id}`}>
                {f.name}
              </button>
              <button
                type="button"
                onClick={(e) => handleDeleteFolder(f.folder_id, e)}
                className="opacity-0 group-hover:opacity-100 hover:text-red-300"
                title="Delete folder"
                data-testid={`folder-delete-${f.folder_id}`}
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
          {hasActiveFilter && (
            <button
              type="button"
              onClick={handleSaveFolder}
              className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border border-dashed border-emerald-500/50 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/30"
              data-testid="folder-save-current"
            >
              <FolderPlus className="w-3 h-3" />
              Save current filter
            </button>
          )}
        </div>
      )}

      {/* Tag filter chips (above bulk bar) */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="tag-filter-row">
          <Tag className="w-3 h-3 text-muted-foreground" />
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-muted-foreground mr-1">Tags</span>
          {activeTag && (
            <button
              type="button"
              onClick={() => setActiveTag(null)}
              className="text-[10px] text-muted-foreground hover:text-foreground underline"
              data-testid="tag-filter-clear"
            >
              Clear filter
            </button>
          )}
          {allTags.slice(0, 12).map(({ tag, count }) => (
            <button
              key={tag}
              type="button"
              onClick={() => setActiveTag(activeTag === tag ? null : tag)}
              className={cn(
                'flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors border',
                activeTag === tag
                  ? 'bg-emerald-500 text-white border-emerald-500'
                  : 'bg-muted/50 text-muted-foreground hover:bg-muted border-transparent',
              )}
              data-testid={`tag-filter-${tag}`}
            >
              {tag}
              <span className="text-[9px] font-mono opacity-70">{count}</span>
            </button>
          ))}
        </div>
      )}

      {/* Bulk action bar */}
      {selectedItems.length > 0 && (
        <div
          className="flex items-center gap-2 rounded-lg border-2 border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/30 px-3 py-2"
          data-testid="bulk-action-bar"
        >
          <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
            {selectedItems.length} selected
          </span>
          <Button size="sm" variant="ghost" onClick={selectAllVisible} className="h-7 text-xs" data-testid="bulk-select-all">
            Select all visible ({filtered.length})
          </Button>
          <Button size="sm" variant="ghost" onClick={clearSelection} className="h-7 text-xs" data-testid="bulk-clear">
            <X className="w-3 h-3 mr-1" /> Clear
          </Button>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => handleBulkPin(true)} className="h-7 text-xs gap-1.5" data-testid="bulk-pin">
            <Pin className="w-3 h-3" /> Pin
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleBulkPin(false)} className="h-7 text-xs gap-1.5" data-testid="bulk-unpin">
            <Pin className="w-3 h-3" /> Unpin
          </Button>
          <Button size="sm" variant="outline" onClick={handleBulkAddTag} className="h-7 text-xs gap-1.5" data-testid="bulk-tag">
            <Tag className="w-3 h-3" /> Add tag
          </Button>
          <Button size="sm" variant="destructive" onClick={handleBulkDelete} className="h-7 text-xs gap-1.5" data-testid="bulk-delete">
            <Trash2 className="w-3 h-3" /> Delete
          </Button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-20" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm text-muted-foreground">
              {search.trim() ? `No conversations match "${search}"` : 'No conversations yet — start a chat anywhere to see it here.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {filtered.map((c) => {
            const meta = SOURCE_META[c.source] || SOURCE_META.ai_studio;
            const Icon = meta.icon;
            const isSelected = selectedKeys.has(selectionKey(c));
            return (
              <div
                key={`${c.source}-${c.id}`}
                role="button"
                tabIndex={0}
                onClick={() => navigate(c.deep_link)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(c.deep_link); }}
                className={cn(
                  'group flex items-start gap-3 rounded-xl border p-3.5 transition-all text-left cursor-pointer',
                  isSelected
                    ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/25 ring-2 ring-emerald-500/20'
                    : c.pinned
                      ? 'border-amber-400/50 bg-amber-50/40 dark:bg-amber-950/15 hover:border-amber-500/70'
                      : 'border bg-card hover:bg-muted/40 hover:border-emerald-500/40',
                )}
                data-testid={`all-conversations-row-${c.source}-${c.id}`}
              >
                {/* Bulk-select checkbox — stop ALL event bubbling so clicking
                    the checkbox doesn't also navigate to the conversation. */}
                <div
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="pt-1 flex-shrink-0"
                  data-testid={`row-checkbox-${c.source}-${c.id}`}
                >
                  <Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(c)} />
                </div>

                <div className={cn(
                  'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border relative',
                  meta.badgeClass,
                )}>
                  <Icon className="w-4 h-4" />
                  {c.pinned && (
                    <Pin className="absolute -top-1 -right-1 w-3 h-3 text-amber-500 fill-amber-500" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">{c.title}</p>
                    <Badge variant="outline" className={cn('text-[9px] font-mono uppercase tracking-wider flex-shrink-0', meta.badgeClass)}>
                      {meta.label}
                    </Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
                    <Clock className="w-3 h-3" />
                    {timeAgo(c.updated_at)}
                    {c.message_count != null && (
                      <span>· {c.message_count} message{c.message_count === 1 ? '' : 's'}</span>
                    )}
                    {c.model && (
                      <span className="truncate max-w-[140px]">· {c.model.split('/').pop()}</span>
                    )}
                  </p>

                  {/* Inline tag chips */}
                  {(c.tags?.length > 0 || true) && (
                    <div
                      className="flex flex-wrap gap-1 mt-1.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {(c.tags || []).map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setActiveTag(t); }}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-700 dark:text-emerald-300 text-[10px] font-medium"
                          data-testid={`row-tag-${c.source}-${c.id}-${t}`}
                        >
                          <Tag className="w-2.5 h-2.5" />
                          {t}
                        </button>
                      ))}
                      <TagEditorPopover
                        currentTags={c.tags || []}
                        allTags={allTags}
                        onSave={(newTags) => handleSetTags(c, newTags)}
                        trigger={
                          <button
                            type="button"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-dashed border-muted-foreground/40 text-muted-foreground hover:border-emerald-500/60 hover:text-emerald-600 text-[10px] opacity-60 group-hover:opacity-100 transition-opacity"
                            title="Edit tags"
                            data-testid={`row-edit-tags-${c.source}-${c.id}`}
                          >
                            <Tag className="w-2.5 h-2.5" />
                            {(c.tags || []).length === 0 ? 'Tag' : 'Edit'}
                          </button>
                        }
                      />
                    </div>
                  )}
                </div>

                {/* Pin toggle — visible on hover OR when already pinned */}
                <button
                  type="button"
                  onClick={(e) => handleTogglePin(e, c)}
                  className={cn(
                    'p-1.5 rounded-md transition-all',
                    c.pinned
                      ? 'opacity-100 hover:bg-amber-100 dark:hover:bg-amber-900/20'
                      : 'opacity-0 group-hover:opacity-100 hover:bg-amber-50 dark:hover:bg-amber-900/20',
                  )}
                  title={c.pinned ? 'Unpin' : 'Pin to top'}
                  data-testid={`all-conversations-pin-${c.source}-${c.id}`}
                >
                  {c.pinned
                    ? <Pin className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                    : <Pin className="w-3.5 h-3.5 text-muted-foreground" />}
                </button>
                <button
                  type="button"
                  onClick={(e) => handleDelete(e, c)}
                  className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 transition-all"
                  title="Delete conversation"
                  data-testid={`all-conversations-delete-${c.source}-${c.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </button>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-emerald-500 flex-shrink-0 transition-colors mt-1" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
